import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { allProjects } from '../data/projectsData';
import AnimatedSectionTitle from '../components/AnimatedSectionTitle';
import ProjectCard from '../components/ProjectCard/ProjectCard';
import '../components/Projects.css';
import './ProjectsPage.css';

// Matches the HomeV2 slide-in panel's own transition duration: when
// embedded there, cards render blank first while the panel is still
// sliding, then form in, then (a beat later) their videos fade in and
// start playing. Keeps the slide itself light on the GPU instead of
// decoding N videos at the exact moment it's animating.
const PANEL_SETTLE_DELAY = 650;
const CARD_STAGGER = 0.08;

interface ProjectsPageProps {
  theme?: string;
  hideHeader?: boolean;
  deferredReveal?: boolean;
}

const ProjectsPage: React.FC<ProjectsPageProps> = ({ theme = 'dark', hideHeader = false, deferredReveal = false }) => {
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const isDark = theme === 'dark';
  const navigate = useNavigate();

  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Extract all unique tags from projects
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    allProjects.forEach(project => {
      project.tags?.forEach(tag => tags.add(tag));
    });
    // Sort with special tags first
    return Array.from(tags).sort((a, b) => {
      const specialTag = 'National Designathon Winner';
      if (a === specialTag) return -1;
      if (b === specialTag) return 1;
      return a.localeCompare(b);
    });
  }, []);

  // Filter projects based on selected filters
  const filteredProjects = useMemo(() => {
    if (selectedFilters.length === 0) {
      return allProjects;
    }
    return allProjects.filter(project =>
      selectedFilters.every(filter =>
        project.tags?.includes(filter)
      )
    );
  }, [selectedFilters]);

  useEffect(() => {
    if (!deferredReveal) return;

    const cards = cardRefs.current.filter((el): el is HTMLDivElement => el !== null);
    gsap.set(cards, { opacity: 0, x: 80 });

    const timer = setTimeout(() => {
      gsap.to(cards, {
        opacity: 1,
        x: 0,
        duration: 0.55,
        ease: 'power3.out',
        stagger: CARD_STAGGER,
      });

      cards.forEach((_, i) => {
        const delay = (i * CARD_STAGGER + 0.45) * 1000;
        setTimeout(() => {
          const video = videoRefs.current[i];
          if (!video) return;
          video.play().catch(() => {});
          gsap.to(video, { opacity: 1, duration: 0.5, ease: 'power2.out' });
        }, delay);
      });
    }, PANEL_SETTLE_DELAY);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deferredReveal, filteredProjects.length]);

  const handleFilterToggle = (tag: string) => {
    setSelectedFilters(prev =>
      prev.includes(tag)
        ? prev.filter(f => f !== tag)
        : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSelectedFilters([]);
  };

  const handleProjectClick = (route: string | null | undefined) => {
    if (route) {
      navigate(route);
    }
  };

  return (
    <div className="projects-page">
      <section className="projects-section">
        {!hideHeader && (
          <>
            <AnimatedSectionTitle title="All Projects" className="projects-title" />

            <div className="projects-header-row">
              <p className="projects-page-subtitle">
                A collection of my work, from designathons to product design case studies
              </p>

              {/* Compact Filter Dropdown */}
              <div className="filter-dropdown-container">
                <button
                  className="filter-dropdown-trigger"
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                  </svg>
                  Filter
                  {selectedFilters.length > 0 && (
                    <span className="filter-count-badge">{selectedFilters.length}</span>
                  )}
                </button>

                {isFilterOpen && (
                  <div className="filter-dropdown-menu">
                    <div className="filter-dropdown-header">
                      <span>Filter by tags</span>
                      {selectedFilters.length > 0 && (
                        <button className="clear-filters-link" onClick={clearFilters}>
                          Clear all
                        </button>
                      )}
                    </div>
                    <div className="filter-dropdown-tags">
                      {allTags.map(tag => (
                        <button
                          key={tag}
                          className={`filter-dropdown-tag ${selectedFilters.includes(tag) ? 'active' : ''}`}
                          onClick={() => handleFilterToggle(tag)}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Projects count */}
        {selectedFilters.length > 0 && (
          <div className="projects-count">
            Showing {filteredProjects.length} of {allProjects.length} {filteredProjects.length === 1 ? 'project' : 'projects'}
          </div>
        )}

        <div className="projects-list">
          {filteredProjects.map((project, index) => (
            <ProjectCard
              key={index}
              project={project}
              isDark={isDark}
              onClick={handleProjectClick}
              deferredReveal={deferredReveal}
              cardRef={(el) => { cardRefs.current[index] = el; }}
              videoRef={(el) => { videoRefs.current[index] = el; }}
            />
          ))}
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
